import React, { useState, useEffect } from 'react'
import { getProfile, updateProfile, uploadAvatar, updateCompanions } from '../api'
import { useLanguage } from '../LanguageContext'

const STORAGE_BASE = 'https://api.mwuilu.com/public/'

const INSTITUTION_OPTIONS = [
  { value: 'Private Client', labelKey: 'instEmbassy' },
  { value: 'Family Office', labelKey: 'instConsulateGeneral' },
  { value: 'Private Office', labelKey: 'instConsulate' },
  { value: 'Corporate Entity', labelKey: 'instPermanentMission' },
  { value: 'Government / Ministry', labelKey: 'instInternationalOrg' },
  { value: 'Embassy / Consulate', labelKey: 'instPrivateOffice' },
  { value: 'Permanent Mission', labelKey: 'instCorporate' },
  { value: 'International Organization', labelKey: 'instGovernmentMinistry' },
  { value: 'Royal Household', labelKey: 'instRoyalHousehold' },
  { value: 'Representative(on behalf of a principal)', labelKey: 'instrepresent' },
  { value: 'Other', labelKey: 'instOther' },
]

const ROLE_OPTIONS = [
  { value: 'Spouse',        labelKey: 'roleSpouse' },
  { value: 'Partner',       labelKey: 'rolePartner' },
  { value: 'Friend',        labelKey: 'roleFriend' },
  { value: 'Family Member', labelKey: 'roleFamilyMember' },
  { value: 'Parent',        labelKey: 'roleParent' },
  { value: 'Child',         labelKey: 'roleChild' },
  { value: 'Sibling',       labelKey: 'roleSibling' },
  { value: 'Assistant',     labelKey: 'roleAssistant' },
  { value: 'Colleague',     labelKey: 'roleColleague' },
  { value: 'Client',        labelKey: 'roleClient' },
  { value: 'Caregiver',     labelKey: 'roleCaregiver' },
  { value: 'Other',         labelKey: 'roleOther' },
]

const ALL_COUNTRIES = [
  'Afghanistan','Albania','Algeria','Andorra','Angola','Argentina','Armenia','Australia',
  'Austria','Azerbaijan','Bahrain','Bangladesh','Belarus','Belgium','Belize','Benin',
  'Bhutan','Bolivia','Bosnia and Herzegovina','Botswana','Brazil','Brunei','Bulgaria',
  'Burkina Faso','Burundi','Cambodia','Cameroon','Canada','Cape Verde','Chad','Chile',
  'China','Colombia','Comoros','Congo','Costa Rica','Croatia','Cuba','Cyprus',
  'Czech Republic','Denmark','Djibouti','Dominican Republic','Ecuador','Egypt',
  'El Salvador','Eritrea','Estonia','Ethiopia','Fiji','Finland','France','Gabon',
  'Gambia','Georgia','Germany','Ghana','Greece','Guatemala','Guinea','Haiti','Honduras',
  'Hungary','Iceland','India','Indonesia','Iran','Iraq','Ireland','Israel','Italy',
  'Jamaica','Japan','Jordan','Kazakhstan','Kenya','Kuwait','Kyrgyzstan','Laos','Latvia',
  'Lebanon','Lesotho','Liberia','Libya','Liechtenstein','Lithuania','Luxembourg',
  'Madagascar','Malawi','Malaysia','Maldives','Mali','Malta','Mauritania','Mauritius',
  'Mexico','Moldova','Monaco','Mongolia','Montenegro','Morocco','Mozambique','Myanmar',
  'Namibia','Nepal','Netherlands','New Zealand','Nicaragua','Niger','Nigeria','Norway',
  'Oman','Pakistan','Palestine','Panama','Paraguay','Peru','Philippines','Poland',
  'Portugal','Qatar','Romania','Russia','Rwanda','Saudi Arabia','Senegal','Serbia',
  'Sierra Leone','Singapore','Slovakia','Slovenia','Somalia','South Africa','South Korea',
  'Spain','Sri Lanka','Sudan','Sweden','Switzerland','Syria','Taiwan','Tajikistan',
  'Tanzania','Thailand','Togo','Trinidad and Tobago','Tunisia','Turkey','Turkmenistan',
  'Uganda','Ukraine','United Arab Emirates','United Kingdom','United States','Uruguay',
  'Uzbekistan','Venezuela','Vietnam','Yemen','Zambia','Zimbabwe',
]

function buildAvatarUrl(path) {
  if (!path) return null
  if (path.startsWith('http') || path.startsWith('blob:')) return path
  return STORAGE_BASE + path.replace(/^\//, '')
}

function roleLabel(t, role) {
  const row = ROLE_OPTIONS.find(r => r.value === role)
  return row ? t(row.labelKey) : (role || t('roleUnspecified'))
}

function CompanionCard({ companion, index, onUpdate, onRemove }) {
  const { t } = useLanguage()
  const [open, setOpen] = useState(false)
  return (
    <div className="cnt-companion">
      <div className="cnt-companion__header">
        <div className="cnt-companion__avatar">{companion.initials || '?'}</div>
        <div className="cnt-companion__info">
          <span className="cnt-companion__name">{companion.name || t('companionDefaultName', { n: index + 1 })}</span>
          <span className="cnt-companion__role">{roleLabel(t, companion.role)}</span>
        </div>
        <div className="cnt-companion__actions">
          <button type="button" className="cnt-icon-btn" onClick={() => setOpen(o => !o)} aria-label={t('editCompanion')}>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M9.5 1.5l2 2L4 11H2v-2L9.5 1.5Z" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <button type="button" className="cnt-icon-btn cnt-icon-btn--danger" onClick={() => onRemove(index)} aria-label={t('removeCompanion')}>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M2 2l9 9M11 2L2 11" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round"/></svg>
          </button>
        </div>
      </div>
      {open && (
        <div className="cnt-companion__form">
          <div className="cnt-row-2">
            <div className="cnt-field">
              <label className="cnt-label">{t('fullName')}</label>
              <input className="cnt-input" value={companion.name} onChange={e => onUpdate(index, 'name', e.target.value)} placeholder={t('asOnPassport')} />
            </div>
            <div className="cnt-field">
              <label className="cnt-label">{t('relationship')}</label>
              <select className="cnt-select" value={companion.role} onChange={e => onUpdate(index, 'role', e.target.value)}>
                <option value="">{t('selectEllipsis')}</option>
                {ROLE_OPTIONS.map(r => (
                  <option key={r.value} value={r.value}>{t(r.labelKey)}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="cnt-row-2">
            <div className="cnt-field">
              <label className="cnt-label">{t('passportNumber')}</label>
              <input className="cnt-input" value={companion.passport || ''} onChange={e => onUpdate(index, 'passport', e.target.value)} placeholder={t('passportNumberPlaceholder')} />
            </div>
            <div className="cnt-field">
              <label className="cnt-label">{t('nationality')}</label>
              <select className="cnt-select" value={companion.nationality || ''} onChange={e => onUpdate(index, 'nationality', e.target.value)}>
                <option value="">{t('selectEllipsis')}</option>
                {ALL_COUNTRIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Props ───────────────────────────────────────────────────
// onBack         fn()
// onAvatarUpdate fn(fullAvatarUrl | null)   ← NEW: notifies parent of avatar changes
// ─────────────────────────────────────────────────────────────
export default function MemberProfile({ onBack, onAvatarUpdate }) {
  const { t } = useLanguage()

  const [loading,      setLoading]      = useState(true)
  const [saving,       setSaving]       = useState(false)
  const [saveMsg,      setSaveMsg]      = useState('')
  const [saveError,    setSaveError]    = useState('')
  const [nameChanged,  setNameChanged]  = useState(false)

  const [origName,     setOrigName]     = useState('')
  const [fullName,     setFullName]     = useState('')
  const [email,        setEmail]        = useState('')
  const [phone,        setPhone]        = useState('')
  const [country,      setCountry]      = useState('')
  const [instType,     setInstType]     = useState('')
  const [instOther,    setInstOther]    = useState('')
  const [instName,     setInstName]     = useState('')
  const [jobTitle,     setJobTitle]     = useState('')
  const [position,     setPosition]     = useState('')
  const [represents,   setRepresents]   = useState('')
  const [joinDate,     setJoinDate]     = useState('')
  const [memberId,     setMemberId]     = useState('')
  const [initials,     setInitials]     = useState('')
  const [avatarUrl,    setAvatarUrl]    = useState(null)
  const [avatarPreview,setAvatarPreview]= useState(null)

  const [companions,   setCompanions]   = useState([])

  useEffect(() => {
    setLoading(true)
    getProfile()
      .then(({ profile, companions: comp }) => {
        setOrigName(profile.name)
        setFullName(profile.name)
        setEmail(profile.email       || '')
        setPhone(profile.phone       || '')
        setCountry(profile.country   || '')
        setInstType(profile.institution || '')
        setInstOther(profile.inst_other || '')
        setInstName(profile.inst_name   || '')
        setJobTitle(profile.job_title   || '')
        setPosition(profile.position    || '')
        setRepresents(profile.represents || '')
        setJoinDate(profile.join_date    || '')
        setMemberId(profile.member_id    || '')
        setInitials(profile.initials     || '')
        setAvatarUrl(profile.avatar_url  || null)
        setCompanions(comp || [])
      })
      .catch(() => setSaveError(t('failedLoadProfile')))
      .finally(() => setLoading(false))
  }, [])

  const handleNameChange = (val) => {
    setFullName(val)
    setNameChanged(val !== origName)
  }

  const handleAvatarChange = async (file) => {
    if (!file) return

    // Show blob preview immediately while upload is in flight
    const blobUrl = URL.createObjectURL(file)
    setAvatarPreview(blobUrl)

    // Notify parent with the blob preview straight away so sidebar updates instantly
    onAvatarUpdate?.(blobUrl)

    try {
      const { avatar_url } = await uploadAvatar(file)

      // Store the relative path returned by the API
      setAvatarUrl(avatar_url)
      setAvatarPreview(null)

      // Revoke the blob to free memory, then notify parent with the permanent URL
      URL.revokeObjectURL(blobUrl)
      onAvatarUpdate?.(buildAvatarUrl(avatar_url))
    } catch {
      setSaveError(t('avatarUploadFailed'))
      // Roll back preview on failure; leave parent with the blob (still valid for this session)
    }
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    setSaveMsg('')
    setSaveError('')
    try {
      await updateProfile({
        name: fullName, email, phone, country,
        institution: instType, inst_other: instOther,
        inst_name: instName, job_title: jobTitle,
        position, represents,
      })
      await updateCompanions(companions.map(c => ({
        name: c.name, role: c.role,
        passport: c.passport, nationality: c.nationality,
      })))

      setOrigName(fullName)
      setNameChanged(false)
      setSaveMsg(t('profileUpdated'))
      setTimeout(() => setSaveMsg(''), 3500)
    } catch (err) {
      setSaveError(err.message || t('failedSave'))
    } finally {
      setSaving(false)
    }
  }

  const addCompanion = () => {
    setCompanions(prev => [...prev, { name: '', role: '', initials: '', passport: '', nationality: '' }])
  }

  const updateCompanion = (i, field, val) => {
    setCompanions(prev => prev.map((c, idx) => {
      if (idx !== i) return c
      const updated = { ...c, [field]: val }
      if (field === 'name') {
        updated.initials = val.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
      }
      return updated
    }))
  }

  const removeCompanion = (i) => setCompanions(prev => prev.filter((_, idx) => idx !== i))

  // Blob preview takes priority; otherwise build the full URL from the stored relative path
  const avatarSrc = avatarPreview || buildAvatarUrl(avatarUrl)

  if (loading) {
    return (
      <div className="db-page cnt-page">
        <div className="svc-submitted" style={{ opacity: 0.4 }}>
          <div className="svc-submitted__icon">◌</div>
          <p className="svc-submitted__body">{t('loadingProfile')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="db-page cnt-page profilepage">

      <div className="cnt-header">
        <button className="cnt-back" onClick={onBack} aria-label={t('back')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {t('dashboard')}
        </button>
        <div className="cnt-header__title-wrap">
          <p className="cnt-header__overline">{t('profileMemberOverline')}</p>
          <h1 className="cnt-header__title">{t('profileTitle')}</h1>
        </div>
      </div>

      <form className="cnt-form" onSubmit={handleSave} noValidate>

        <div className="cnt-section">
          <div className="cnt-profile-top">
            <div className="cnt-avatar-wrap">
              <div className="cnt-avatar">
                {avatarSrc
                  ? <img src={avatarSrc} alt={t('profileTitle')} className="cnt-avatar__img" />
                  : <span className="cnt-avatar__initials">{initials}</span>
                }
              </div>
              <label className="cnt-avatar-btn" aria-label={t('ariaChangePhoto')}>
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => e.target.files[0] && handleAvatarChange(e.target.files[0])} />
                {t('changePhoto')}
              </label>
            </div>
            <div className="cnt-member-id-block">
              <span className="cnt-label">{t('memberIdLabel')}</span>
              <span className="cnt-member-id">{memberId}</span>
              <span className="cnt-member-id-note">{t('memberIdNote')}</span>
              <span className="cnt-member-since">{t('memberSince', { date: joinDate || '—' })}</span>
            </div>
          </div>
        </div>

        <div className="cnt-section">
          <p className="cnt-section__label">{t('personalDetails')}</p>

          <div className="cnt-field">
            <label className="cnt-label" htmlFor="fullName">{t('fullName')}</label>
            <input id="fullName" className="cnt-input" value={fullName} onChange={e => handleNameChange(e.target.value)} placeholder={t('placeholderPassportName')} />
            <span className="cnt-field-hint">{t('hintPassportName')}</span>
            {nameChanged && (
              <span className="cnt-field-alert">{t('nameChangeAlert')}</span>
            )}
          </div>

          <div className="cnt-row-2">
            <div className="cnt-field">
              <label className="cnt-label" htmlFor="email">{t('emailAddress')}</label>
              <input id="email" type="email" className="cnt-input" value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            <div className="cnt-field" style={{ marginTop: 0 }}>
              <label className="cnt-label" htmlFor="phone">{t('phoneNumber')}</label>
              <input id="phone" type="tel" className="cnt-input" value={phone} onChange={e => setPhone(e.target.value)} placeholder={t('phonePlaceholder')} />
            </div>
          </div>

          <div className="cnt-field">
            <label className="cnt-label" htmlFor="country">{t('countryOfResidence')}</label>
            <select id="country" className="cnt-select" value={country} onChange={e => setCountry(e.target.value)}>
              <option value="">{t('selectCountry')}</option>
              {ALL_COUNTRIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div className="cnt-section">
          <p className="cnt-section__label">{t('professionalDetails')}</p>

          <div className="cnt-field">
            <label className="cnt-label" htmlFor="instType">{t('institutionType')}</label>
            <select id="instType" className="cnt-select" value={instType} onChange={e => setInstType(e.target.value)}>
              <option value="">{t('selectEllipsis')}</option>
              {INSTITUTION_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
              ))}
            </select>
          </div>

          {instType === 'Other' && (
            <div className="cnt-field">
              <label className="cnt-label" htmlFor="instOther">{t('institutionOther')}</label>
              <input id="instOther" className="cnt-input" value={instOther} onChange={e => setInstOther(e.target.value)} />
            </div>
          )}

          <div className="cnt-field">
            <label className="cnt-label" htmlFor="instName">{t('institutionName')}</label>
            <input id="instName" className="cnt-input" value={instName} onChange={e => setInstName(e.target.value)} placeholder={t('institutionNamePlaceholder')} />
          </div>

          <div className="cnt-row-2">
            <div className="cnt-field">
              <label className="cnt-label" htmlFor="jobTitle">{t('jobTitle')}</label>
              <input id="jobTitle" className="cnt-input" value={jobTitle} onChange={e => setJobTitle(e.target.value)} placeholder={t('jobTitlePlaceholder')} />
            </div>
            <div className="cnt-field" style={{ marginTop: 0 }}>
              <label className="cnt-label" htmlFor="position">{t('position')}</label>
              <input id="position" className="cnt-input" value={position} onChange={e => setPosition(e.target.value)} placeholder={t('positionPlaceholder')} />
            </div>
          </div>

          <div className="cnt-field">
            <label className="cnt-label" htmlFor="represents">{t('countryRepresented')}</label>
            <select id="represents" className="cnt-select" value={represents} onChange={e => setRepresents(e.target.value)}>
              <option value="">{t('selectCountry')}</option>
              {ALL_COUNTRIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div className="cnt-section">
          <div className="cnt-section-header">
            <p className="cnt-section__label">{t('savedCompanions')}</p>
            <button type="button" className="cnt-add-btn" onClick={addCompanion}>{t('addCompanion')}</button>
          </div>
          <p className="cnt-section-note">{t('companionsHelp')}</p>
          {companions.length === 0 && <p className="cnt-empty">{t('noCompanionsYet')}</p>}
          <div className="cnt-companions-list">
            {companions.map((comp, i) => (
              <CompanionCard key={i} companion={comp} index={i} onUpdate={updateCompanion} onRemove={removeCompanion} />
            ))}
          </div>
        </div>

        <div className="cnt-form__footer">
          {saveMsg   && <span className="cnt-save-confirm">{saveMsg}</span>}
          {saveError && <span className="cnt-field-alert">{saveError}</span>}
          <button type="submit" className="cnt-btn-primary cnt-btn-primary--wide" disabled={saving}>
            {saving ? t('saving') : t('saveChanges')}
          </button>
        </div>

      </form>
    </div>
  )
}